import { intersection, set } from "@prismicio/editor-support/URLSearchParams"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

import { teamSpaceManagementPermission } from "../../app/constants"
import { useRole } from "../../app/services/user"

/**
 * Creates new URLSearchParams for Team Space navigation.
 *
 * @param currentSearchParams - The current URL search parameters
 * @param teamSpaceId - The ID of the Team Space you want to create params for
 * @param teamSpaceCustomTypes - Array of Custom Type IDs allowed in the Team Space
 * you want to create params for
 */
export function getSearchParamsForTeamSpaceNavigation(
  currentSearchParams: URLSearchParams,
  teamSpaceId: string,
  teamSpaceCustomTypes: string[],
) {
  const newSearchParams = new URLSearchParams(currentSearchParams)
  set(newSearchParams, "space", teamSpaceId)
  intersection(newSearchParams, "customTypes", teamSpaceCustomTypes)
  return newSearchParams
}

export const useTeamSpaceNavigation = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { pathname } = useLocation()

  return {
    /**
     * Navigates to a Team Space.
     * @param teamSpaceId - The ID of the Team Space you want to navigate to
     * @param teamSpaceCustomTypes - Array of Custom Type IDs allowed in the Team Space
     * you want to navigate to.
     */
    navigateToTeamSpace: (
      teamSpaceId: string,
      teamSpaceCustomTypes: string[],
    ) => {
      navigate({
        pathname,
        search: getSearchParamsForTeamSpaceNavigation(
          searchParams,
          teamSpaceId,
          teamSpaceCustomTypes,
        ).toString(),
      })
    },
  }
}

/**
 * Helper hook to check if the user can manage Spaces
 * (if the user can create or modify a Team Space)
 */
export function useCanManageSpaces() {
  const { role } = useRole()
  return teamSpaceManagementPermission[role]
}
